import type { PermissionCheckRequest } from "../api/aclApi";
import { resolveItemOwnerUid } from "../component/resolveItemOwnerUid";

export type ItemPermissionAction = "read" | "update" | "delete" | "delegate";

const DEFAULT_ITEM_PERMISSION_ACTIONS: ItemPermissionAction[] = [
  "read",
  "update",
  "delete",
  "delegate",
];

/** Same slice as DataTable client pagination. */
export function slicePagedTableRows<T>(rows: T[], page: number, pageSize: number): T[] {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  const safePage = Math.max(1, page);
  const safeSize = Math.max(1, pageSize);
  const start = (safePage - 1) * safeSize;
  return rows.slice(start, start + safeSize);
}

/** Visible page rows plus extras (selection, current item) without duplicate uids. */
export function mergeRowsForItemPermissionChecks(
  primaryRows: unknown[],
  extraRows: unknown[]
): unknown[] {
  const seen = new Set<string>();
  const merged: unknown[] = [];

  for (const row of [...primaryRows, ...extraRows]) {
    if (!row || typeof row !== "object") continue;
    const uid = (row as { uid?: string }).uid;
    if (uid == null || uid === "") continue;
    const id = String(uid).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(row);
  }

  return merged;
}

/**
 * Builds bulk ACL requests for visible table rows (one block of actions per item).
 */
export function buildItemPermissionBulkRequests(
  rows: unknown[],
  collectionUid: string | null | undefined,
  actions: ItemPermissionAction[] = DEFAULT_ITEM_PERMISSION_ACTIONS
): {
  requests: PermissionCheckRequest[];
  indexMap: Map<string, number>;
  actionsPerItem: ItemPermissionAction[];
} {
  const requests: PermissionCheckRequest[] = [];
  const indexMap = new Map<string, number>();
  const actionsPerItem = actions.length > 0 ? actions : DEFAULT_ITEM_PERMISSION_ACTIONS;

  const baseItemRequest = (
    action: ItemPermissionAction,
    uid: string,
    ownerUid?: string
  ): PermissionCheckRequest => ({
    action,
    targetType: "item",
    targetId: uid,
    ...(collectionUid && { collectionUid }),
    ...(ownerUid ? { itemOwnerId: ownerUid } : {}),
  });

  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const uid = (row as { uid?: string }).uid;
    if (!uid || indexMap.has(uid)) continue;
    indexMap.set(uid, requests.length);
    const ownerUid = resolveItemOwnerUid(row as Record<string, unknown>);
    for (const action of actionsPerItem) {
      requests.push(baseItemRequest(action, uid, ownerUid));
    }
  }

  return { requests, indexMap, actionsPerItem };
}
